// Overlay worker pool.
//
// Pixel-scene bitmaps (pixel_scene_generation.js "Bitmaps are BUILT IN THE
// OVERLAY WORKER") and edge-decal tiles (edge_decal_layer.js) are built by a
// pool of instances of the overlay worker, each decoding the scenes it is
// asked for on its own. On the page's single FIFO overlay worker they queued
// behind overlay builds and the recolor job (seconds at load) and filled the
// view in one at a time. Neither job needs the tile layers -- a scene build
// needs only the scene metadata and the settings, a decal stamp the biome map
// on top -- so the pool workers are never sent them.
//
// This module knows nothing about the app: telescope's page (overlay_manager.js)
// and any other host of js/terrain_view.js share it. The caches it feeds are
// module state (pixel_scene_generation.js, edge_decal_layer.js), so there is
// one pool per realm; starting it twice returns the same workers.
import { frameSlo } from './frame_slo.js';
import { loadTimeline } from './load_timeline.js';
import { PIXEL_SCENE_DATA, putPixelSceneBitmaps, putSceneMaterialMap, setPixelSceneBitmapRequester } from './pixel_scene_generation.js';
import { defaultOverlayWorkerCount, takeWorker } from './prespawn.js';
import { renderTrace } from './render_hud.js';

export { defaultOverlayWorkerCount };

let workers = null;
const sceneListeners = new Set();
const replyHandlers = new Map();   // message type -> fn(msg)
let syncedBiomeData = null;

// Which worker last built from each scene. A worker decodes a scene's PNG the
// first time it is asked for it and keeps the pixels, so a scene's next job --
// its material map after its bitmaps, another variant of it -- costs a decode
// less on the worker that has them.
const sceneWorker = new Map();   // scene key -> worker
// Work is counted in pixels, not jobs: a scene build costs about its area, and
// the scenes of one world run from 10x10 to 2560x2392. Counted in jobs, two of
// the largest landed on one worker and held up everything queued behind them
// for most of a second while other workers sat idle.
const jobWeight = (msg) => {
	const data = msg.key ? PIXEL_SCENE_DATA[msg.key] : null;
	return data ? Math.max(32 * 32, data.width * data.height) : OTHER_JOB_WEIGHT;
};
/** A job that is not a scene build (a decal tile: a 320x320 stamp). */
const OTHER_JOB_WEIGHT = 320 * 320;
// How much more pending work than the idlest worker the one holding the scene
// may have before the job goes to the idlest instead.
const AFFINITY_SLACK = 256 * 256;

function post(w, msg, transfer) {
	if (w.ready && w.worker) w.worker.postMessage(msg, transfer ?? []);
	else w.queue.push([msg, transfer ?? []]);
}

/**
 * Starts the pool (once) and routes pixel_scene_generation's bitmap requests to
 * it, least-loaded worker first.
 * @param {object} [opts]
 * @param {number} [opts.count]  worker count; only the first call's is used
 * @returns {Worker[]} the pool
 */
export function startOverlayWorkerPool({ count = defaultOverlayWorkerCount() } = {}) {
	if (workers) return workers;
	workers = Array.from({ length: Math.max(1, count) }, (_, i) => {
		// Held until the worker says its module has loaded (overlay_worker.js
		// READY) -- which a worker the page prespawned (prespawn.js) may already
		// have said: takeWorker replays it into the handler before `worker` is set.
		const w = { worker: null, inflight: 0, load: 0, ready: false, queue: [] };
		// A reply frees its job's share of the load (jobWeight is the same both ways).
		const done = (msg) => {
			w.inflight = Math.max(0, w.inflight - 1);
			w.load = w.inflight ? Math.max(0, w.load - jobWeight(msg)) : 0;
		};
		const onMessage = (msg) => {
			loadTimeline.add(msg.spans);
			if (msg.type === 'READY') {
				w.ready = true;
				if (w.worker) {
					for (const [m, transfer] of w.queue) w.worker.postMessage(m, transfer);
					w.queue = [];
				}
			}
			else if (msg.type === 'JOB_START') renderTrace.stage(msg.traceId, 'running');
			else if (msg.type === 'JOB_DONE') renderTrace.end(msg.traceId, { workerMs: msg.ms });
			else if (msg.type === 'SCENE_BITMAPS') {
				done(msg);
				const t0 = performance.now();
				if (putPixelSceneBitmaps(msg)) for (const fn of sceneListeners) fn(msg);
				frameSlo.work('sceneBitmapsLanded', performance.now() - t0);
			}
			else if (msg.type === 'SCENE_MATERIALS') {
				done(msg);
				if (putSceneMaterialMap(msg)) for (const fn of sceneListeners) fn(msg);
			}
			else if (replyHandlers.has(msg.type)) {
				done({});
				replyHandlers.get(msg.type)(msg);
			}
		};
		w.worker = takeWorker('overlay', i, onMessage);
		w.worker.addEventListener('error', (e) =>
			console.error(`overlay pool worker ${i} failed:`, e.message ?? '(no message)', e.filename ?? '', e.lineno ?? ''));
		return w;
	});
	setPixelSceneBitmapRequester((request) => {
		const kind = request.cmd === 'BUILD_SCENE_MATERIALS' ? ' (mat)' : request.textured ? ' (tex)' : '';
		request.traceId = renderTrace.begin('scene', `${request.key}${kind}`, 'pixelScenes');
		postOverlayPoolJob(request);
	});
	return workers;
}

/** Posts one job to the worker with the least pending work -- or, for a job on
 *  one scene (`msg.key`), to the worker that already holds that scene's pixels
 *  unless it has much more to do. Its reply (a message type that has a handler,
 *  see onOverlayPoolReply) frees its share. */
export function postOverlayPoolJob(msg, transfer) {
	const pool = startOverlayWorkerPool();
	let w = pool[0];
	for (const s of pool) if (s.load < w.load) w = s;
	if (msg.key) {
		const holder = sceneWorker.get(msg.key);
		if (holder && holder.load <= w.load + AFFINITY_SLACK) w = holder;
		else sceneWorker.set(msg.key, w);
	}
	w.inflight++;
	w.load += jobWeight(msg);
	post(w, msg, transfer);
}

/** Routes the pool's replies of one message type (other than scene bitmaps) to `fn`. */
export function onOverlayPoolReply(type, fn) {
	replyHandlers.set(type, fn);
}

/**
 * Calls `fn(msg)` whenever a scene bitmap chain lands in the cache, i.e.
 * whenever a redraw would show something new. Returns the function that
 * removes it.
 */
export function onSceneBitmaps(fn) {
	sceneListeners.add(fn);
	return () => sceneListeners.delete(fn);
}

/** PIXEL_SCENE_DATA as the workers want it: metadata only. The worker decodes
 *  each scene's pixels and art itself the first time it needs them
 *  (ensureScenePixels), so never clone any the main thread holds. */
export function sceneMetadataForWorkers() {
	return Object.fromEntries(Object.entries(PIXEL_SCENE_DATA)
		.map(([k, v]) => [k, (v.imgElement || v.visualArt) ? { ...v, imgElement: null, visualArt: null } : v]));
}

/** Sends the scene metadata to every pool worker (after loadPixelSceneData, and
 *  again whenever the main thread's scene table changes). */
export function syncOverlayPoolMetadata(pixelSceneCache = sceneMetadataForWorkers()) {
	for (const w of startOverlayWorkerPool()) post(w, { cmd: 'SYNC_METADATA', pixelSceneCache });
}

/** Sends the world's biome map to every pool worker, once per world: the decal
 *  stamp gates its seam band on it. A few kilobytes, unlike the tile layers. */
export function syncOverlayPoolWorld(biomeData) {
	if (biomeData === syncedBiomeData) return;
	syncedBiomeData = biomeData;
	for (const w of startOverlayWorkerPool()) post(w, { cmd: 'SYNC_METADATA', biomeData });
}

/** Sends the settings object to every pool worker. */
export function syncOverlayPoolSettings(settings) {
	for (const w of startOverlayWorkerPool()) post(w, { cmd: 'SYNC_SETTINGS', settings });
}

/** Jobs in flight per worker, for the frame log and the benchmarks. */
export function overlayPoolStats() {
	return { workers: workers ? workers.length : 0, inflight: workers ? workers.map(w => w.inflight) : [] };
}
